import { isAbsoluteURL } from 'modules/core/lib/isAbsoluteURL'

import { buildUpsellOriginParam } from 'modules/upselling_kit/utils/handlers'

interface StoredUpsellOrigin {
  request_source: string
  request_source_category?: string
  request_source_component?: string
}

/**
 * Extracts component name from componentType by removing 'in_app_comms_' prefix
 */
export const getComponentName = (type: string): string => {
  return type.replace(/^in_app_comms_/, '')
}

/**
 * Checks if a link is a marketplace link
 */
export const isMarketplaceLink = (link: string): boolean => {
  return link.includes('/discover/modules') || link.includes('/modules/')
}

/**
 * Processes template variables in links ({{email}}, {{company_id}})
 */
export const processLink = (
  link: string | null | undefined,
  email?: string | null,
  companyId?: number | null
): string => {
  if (!link) return ''
  let processedLink = link
  if (email) {
    processedLink = processedLink.replace(/\{\{email\}\}/g, email)
  }
  if (companyId) {
    processedLink = processedLink.replace(/\{\{company_id\}\}/g, String(companyId))
  }
  return processedLink
}

/**
 * Extracts pathname from URL (handles both absolute and relative URLs)
 */
export const extractPathname = (url: string): string => {
  if (!isAbsoluteURL(url)) {
    return url
  }
  try {
    const urlObj = new URL(url)
    return urlObj.pathname + urlObj.search + urlObj.hash
  } catch {
    return url
  }
}

/**
 * Builds marketplace link with upsell_origin parameter
 * Extracts existing upsell_origin from URL if present, otherwise uses localStorage or default
 */
export const buildMarketplaceLink = (
  link: string,
  componentType: string,
  componentName: string,
  storedUpsellOrigin?: StoredUpsellOrigin | null
): string => {
  if (!isMarketplaceLink(link)) return link

  // Extract existing upsell_origin from URL if present
  let existingRequestSource: string | undefined
  let pathname = link
  let searchParams = ''

  if (isAbsoluteURL(link)) {
    try {
      const urlObj = new URL(link)
      pathname = urlObj.pathname + urlObj.hash
      searchParams = urlObj.search

      const params = new URLSearchParams(searchParams)
      const existingUpsellOrigin = params.get('upsell_origin')
      if (existingUpsellOrigin) {
        try {
          const parsed = JSON.parse(decodeURIComponent(existingUpsellOrigin))
          if (parsed?.request_source) {
            existingRequestSource = parsed.request_source
          }
        } catch {
          existingRequestSource = existingUpsellOrigin
        }
      }
      params.delete('upsell_origin')
      searchParams = params.toString()
    } catch {
      // If URL parsing fails, use link as is
    }
  } else {
    const queryIndex = link.indexOf('?')
    if (queryIndex !== -1) {
      pathname = link.substring(0, queryIndex)
      searchParams = link.substring(queryIndex + 1)

      const params = new URLSearchParams(searchParams)
      const existingUpsellOrigin = params.get('upsell_origin')
      if (existingUpsellOrigin) {
        try {
          const parsed = JSON.parse(decodeURIComponent(existingUpsellOrigin))
          if (parsed?.request_source) {
            existingRequestSource = parsed.request_source
          }
        } catch {
          existingRequestSource = existingUpsellOrigin
        }
      }
      params.delete('upsell_origin')
      searchParams = params.toString()
    }
  }

  // Use existing request_source from URL, then localStorage, then default
  const requestSource =
    existingRequestSource ||
    storedUpsellOrigin?.request_source ||
    `From in-app communication ${componentName}`

  // Always build the complete upsell_origin with all three fields
  const componentNameForParam = getComponentName(componentType)
  const upsellOriginParam = buildUpsellOriginParam(
    componentNameForParam,
    requestSource,
    'In-app Communication'
  )

  // Add the new upsell_origin param with all fields
  const separator = searchParams ? '&' : '?'
  return `${pathname}${searchParams ? `?${searchParams}` : ''}${separator}${upsellOriginParam}`
}
